import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';
import App from './App.vue';
import { router } from './router';
import './styles/main.css';

/**
 * MOUNT AFTER THE ROUTER HAS RESOLVED ITS FIRST NAVIGATION.
 *
 * `router.isReady()` matters more than it looks. Vue Router's initial
 * navigation is asynchronous, so an app that mounts immediately renders once
 * against `START_LOCATION` — a route with no name, no path and an EMPTY `meta`.
 *
 * App.vue keys on `route.meta.public || route.meta.bare` to decide whether to
 * draw the shell. Empty meta is falsy, so on every cold load the shell rendered
 * first, including on `/sign-in` and `/auth/callback` where there is no session
 * — mounting the sidebar, and with it `LanguagePicker`, which fires
 * `api.summary()` and takes a 401 nobody asked for.
 *
 * Here that is a wasted request and a flash of the wrong chrome. In an app
 * whose API client redirects on 401 it is much worse: the redirect destroys the
 * callback page before the authorisation code can be exchanged, and sign-in
 * fails with no token request ever sent. Lasti is safe from that second half by
 * accident rather than design — nothing here calls `location.assign` on 401 —
 * and the accident is not worth relying on.
 *
 * `initSession()` only reads sessionStorage, so waiting costs no network round
 * trip and cannot hang on one.
 */
const app = createApp(App)
  .use(createPinia())
  .use(router)
  .use(VueQueryPlugin, {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          // The catalog is not a live feed. Refetching it because a window
          // regained focus costs a round trip and changes nothing.
          refetchOnWindowFocus: false,
          staleTime: 30_000,
          /**
           * One attempt. The retry is the Try again button.
           *
           * A scheduled retry can be PAUSED — TanStack holds it until its
           * online manager says the browser is back — and a paused query never
           * leaves `pending`: no error is recorded, `refetch()` does nothing,
           * and the screen shows "Loading…" until the tab is closed. Observed
           * with the API stopped and `networkMode: 'always'` set, so the
           * setting below is a belt and this is the braces.
           *
           * Retrying was buying very little anyway. The SPA and the API are the
           * same origin (§15.2), so a request that fails has usually met
           * something a second attempt will meet too, and every screen that
           * reads has a visible way to ask again. Failing once and saying so
           * beats succeeding invisibly on the second go.
           */
          retry: false,
          /**
           * Attempt the request, always. Never pause it.
           *
           * TanStack Query's default `networkMode: 'online'` pauses a retrying
           * query when it believes the browser is offline, and a paused query
           * stays `pending` for ever: `isPending` never clears, no error is
           * recorded, and the screen shows "Loading…" until the tab is closed.
           * Found with the API stopped — the order list sat on "Loading…"
           * indefinitely instead of saying the request had failed.
           *
           * Pausing is right for an offline-first app with a cache to fall back
           * on. This has neither: the SPA and the API are the same origin
           * (§15.2), so if the page loaded the API is reachable, and
           * `navigator.onLine` is not evidence of anything much. Attempting and
           * failing gives the screens something true to say.
           */
          networkMode: 'always',
        },
      },
    },
  });

void router.isReady().then(() => app.mount('#app'));
